/**
 * Shape — a structural contract that a dataSource's cells may
 * satisfy. Shapes are probe-based: given an opaque cell value,
 * `shape.probe(value)` returns a type predicate narrowing the
 * value to the canonical type.
 *
 * Shapes are never declared on the descriptor. Provider rows can
 * carry server-owned shape metadata; rows without that tag can still
 * be recognized by probing their cells. A `FormulaDataSource` that
 * synthesizes Event-shaped cells satisfies `EVENT_SHAPE` without any
 * per-row tagging.
 */

import type { Option } from "./option.ts";
import type { CellValue } from "./dtype.ts";
import type { Json } from "./json.ts";

/**
 * A value a shape can probe: a cell value as the stores hold it — wire
 * JSON, or a canonical `CellValue` — or an empty cell.
 */
export type Probed = Option<Json | CellValue>;

/**
 * Branded key that carries the canonical type of a shape at the
 * type level. `ShapeKey<Event>` guarantees downstream call sites
 * see `Event` values.
 */
export class ShapeKey<C> {
  /** A key is process-global (one per shape), so it is frozen. */
  constructor(readonly id: string) {
    Object.freeze(this);
  }
  /** Phantom reference to the canonical type — erased at runtime. */
  declare readonly __shape: C;
}

/**
 * A cell value's shape: its key and a structural probe that recognises a
 * value of it.
 */
export interface Shape<C extends Json | CellValue> {
  readonly key: ShapeKey<C>;
  /** Pure structural probe of a cell value. No side effects. */
  probe(value: Option<Json | CellValue>): value is C;
}

/**
 * Convenience constructor — cuts the ShapeKey + probe boilerplate.
 *
 *   const EVENT = defineShape<Event>("event/v1", (v) => …);
 */
export function defineShape<C extends Json | CellValue>(
  id: string,
  probe: (value: Probed) => value is C,
): Shape<C> {
  return { key: new ShapeKey<C>(id), probe };
}
